import type { ChatMessage } from '@/hooks/useEventMessages';
import { formatMessageTime } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { Badge, Skeleton } from '@startupquickstart/frontend';
import { MessageCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface FacilitatorMessageListProps {
  messages: ChatMessage[];
  isLoading?: boolean;
  teamName?: string;
}

export function FacilitatorMessageList({
  messages,
  isLoading = false,
  teamName,
}: FacilitatorMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (isLoading) {
    return (
      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-12 w-3/4 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
          <MessageCircle className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="font-medium text-foreground mb-1">No messages yet</h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          {teamName
            ? `Send a message to help ${teamName} with their hunt.`
            : 'Select a team to view and send messages.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
      {messages.map((message) => {
        const isFacilitator = message.senderType === 'facilitator';
        const isSystem = message.senderType === 'system';

        if (isSystem) {
          return (
            <div key={message.id} className="flex justify-center">
              <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full whitespace-pre-wrap">
                {message.content}
              </span>
            </div>
          );
        }

        return (
          <div
            key={message.id}
            className={cn('flex gap-3', isFacilitator && 'flex-row-reverse')}
          >
            <div
              className={cn(
                'h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0',
                isFacilitator ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
              )}
            >
              {message.senderName.charAt(0).toUpperCase()}
            </div>
            <div className={cn('max-w-[70%] space-y-1', isFacilitator && 'items-end')}>
              <div
                className={cn('flex items-center gap-2', isFacilitator && 'flex-row-reverse')}
              >
                <span className="text-sm font-medium">{message.senderName}</span>
                {isFacilitator && (
                  <Badge variant="outline" className="text-xs py-0">
                    Staff
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {formatMessageTime(message.createdAt)}
                </span>
              </div>
              <div
                className={cn(
                  'px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words',
                  isFacilitator
                    ? 'bg-primary text-primary-foreground rounded-tr-none'
                    : 'bg-muted rounded-tl-none',
                )}
              >
                {message.content}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}

